/**
 * Cuenta corriente del cliente — ventas/facturas adeudadas.
 * Fuente canónica: cuentas-por-cobrar (cliente_id) + saldo backend.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaEdit,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaMoneyBillWave,
  FaSync
} from 'react-icons/fa';

import clientesService from '../../services/clientes.service';
import ventasService from '../../services/ventas.service';
import configuracionService from '../../services/configuracion.service';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import RegistrarPagoDialog from '../../components/modules/ventas/RegistrarPagoDialog';
import {
  buildFilasCuentaCorriente,
  elegirFilaCuentaParaPago,
  extractVentasCliente,
  resumirCuentaCorriente
} from '../../utils/cuentaCorriente';
import { formatMetodoPagoLabel } from '../../utils/metodoPagoLabels';
import { normalizarDeudaVencidaDias } from '../../utils/dashboardDeuda';

const formatMoney = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value || 0);

const formatFecha = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-AR');
  } catch {
    return '—';
  }
};

const CuentaCorrienteCliente = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagoAutoAbiertoRef = useRef(false);
  const modoPagoDirectoRef = useRef(searchParams.get('action') === 'pago');

  const [cliente, setCliente] = useState(null);
  const [filas, setFilas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ventaPago, setVentaPago] = useState(null);
  const [showPagoDialog, setShowPagoDialog] = useState(false);

  const cargarCuenta = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [clienteData, saldoInfo, cuentasRaw, config] = await Promise.all([
        clientesService.obtenerPorId(id),
        clientesService.calcularSaldoCliente(id),
        ventasService.obtenerCuentasPorCobrar({ cliente_id: id }),
        configuracionService.obtener()
      ]);

      const diasConfig = normalizarDeudaVencidaDias(config?.pos_politicas?.deuda_vencida_dias);
      let ventas = extractVentasCliente(cuentasRaw, id);
      const saldoBackend = parseFloat(saldoInfo?.saldo_actual) || 0;

      // Fallback: dashboard usa cuentas globales; si el filtro por cliente viene vacío pero hay saldo, reintentar.
      if (ventas.length === 0 && saldoBackend > 0) {
        const cuentasGlobal = await ventasService.obtenerCuentasPorCobrar();
        ventas = extractVentasCliente(cuentasGlobal, id);
      }

      const filasCuenta = buildFilasCuentaCorriente(ventas, diasConfig);
      const res = resumirCuentaCorriente(filasCuenta, diasConfig, saldoBackend);

      setCliente(clienteData);
      setFilas(filasCuenta);
      setResumen(res);
    } catch (err) {
      console.error('[CuentaCorriente] Error:', err);
      setError(err?.message || 'No se pudo cargar la cuenta corriente');
      setFilas([]);
      setResumen(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    cargarCuenta();
  }, [cargarCuenta]);

  const nombreCliente = [cliente?.nombre, cliente?.apellido].filter(Boolean).join(' ').trim()
    || cliente?.nombre_completo
    || `Cliente ${String(id || '').slice(0, 8)}`;

  const registrarPago = async (pagoData) => {
    if (!ventaPago?.id) return;
    try {
      await ventasService.registrarPago(ventaPago.id, pagoData);
      toast.success('Pago registrado correctamente');
      setShowPagoDialog(false);
      setVentaPago(null);
      await cargarCuenta();
    } catch (err) {
      console.error('Error al registrar pago:', err);
      toast.error(err?.message || 'Error al registrar el pago');
    }
  };

  const abrirPago = (fila) => {
    setVentaPago({
      id: fila.id,
      numero: fila.numero,
      saldo_pendiente: fila.saldo,
      total: fila.total,
      cliente_nombre: nombreCliente
    });
    setShowPagoDialog(true);
  };

  const elegirFilaParaPago = elegirFilaCuentaParaPago;

  useEffect(() => {
    if (loading || pagoAutoAbiertoRef.current) return;
    if (searchParams.get('action') !== 'pago') return;

    pagoAutoAbiertoRef.current = true;
    setSearchParams({}, { replace: true });

    if (!filas.length) {
      toast.info('Este cliente no tiene ventas adeudadas para registrar pago.');
      return;
    }

    const filaPago = elegirFilaParaPago(filas);
    if (filaPago) {
      abrirPago(filaPago);
    }
  }, [loading, filas, searchParams, setSearchParams]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
        <span className="ml-3 text-e0e3-muted">
          {modoPagoDirectoRef.current ? 'Preparando registro de pago…' : 'Cargando cuenta corriente…'}
        </span>
      </div>
    );
  }

  const ocultarDetalleVentas = modoPagoDirectoRef.current && showPagoDialog;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-e0e3-muted mb-1">Cuenta corriente</p>
          <h1 className="text-2xl font-bold text-e0e3-foreground flex items-center gap-2">
            <FaFileInvoiceDollar className="text-e0e3-accent" />
            {nombreCliente}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button color="secondary" icon={<FaArrowLeft />} onClick={() => navigate('/clientes')}>
            Volver
          </Button>
          <Button color="secondary" icon={<FaSync />} onClick={cargarCuenta}>
            Actualizar
          </Button>
          <Link to={`/clientes/editar/${id}`}>
            <Button color="secondary" icon={<FaEdit />}>
              Editar cliente
            </Button>
          </Link>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
          {error}
          <div className="mt-3">
            <Button size="sm" color="secondary" onClick={cargarCuenta}>
              Reintentar
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-lg border border-e0e3-border/40 bg-e0e3-surface/40 p-4">
              <p className="text-sm text-e0e3-muted">Total adeudado</p>
              <p className="text-2xl font-bold text-e0e3-foreground mt-1">
                {formatMoney(resumen?.totalDeuda)}
              </p>
            </div>
            <div className="rounded-lg border border-red-500/25 bg-red-500/10 p-4">
              <p className="text-sm text-red-200/90 flex items-center gap-1">
                <FaExclamationTriangle size={12} />
                Vencido ({resumen?.diasCriterio}+ días)
              </p>
              <p className="text-2xl font-bold text-red-200 mt-1">
                {formatMoney(resumen?.montoVencido)}
              </p>
            </div>
            <div className="rounded-lg border border-e0e3-border/40 bg-e0e3-surface/40 p-4">
              <p className="text-sm text-e0e3-muted">Ventas adeudadas</p>
              <p className="text-2xl font-bold text-e0e3-foreground mt-1">{resumen?.cantidadVentas ?? 0}</p>
            </div>
            <div className="rounded-lg border border-e0e3-border/40 bg-e0e3-surface/40 p-4">
              <p className="text-sm text-e0e3-muted">Criterio vencimiento</p>
              <p className="text-2xl font-bold text-e0e3-accent mt-1">{resumen?.diasCriterio ?? 30} días</p>
            </div>
          </div>

          {resumen?.desalineadoBackend && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
              El saldo del cliente (${formatMoney(resumen.saldoBackend)}) no coincide con las facturas listadas
              (${formatMoney(resumen.sumaFilas)}). Actualizá la vista o revisá ventas con saldo inconsistente en
              Firestore.
            </div>
          )}

          {(resumen?.cantidadVentas || 0) <= 0 ? (
            <Card>
              <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 p-8 text-center text-emerald-200">
                Este cliente no tiene saldo pendiente en cuenta corriente.
              </div>
            </Card>
          ) : !ocultarDetalleVentas ? (
            <Card title="Facturas / ventas adeudadas" icon={<FaFileInvoiceDollar />}>
              <div className="overflow-x-auto rounded-lg border border-e0e3-border/30">
                <table className="min-w-full text-sm">
                  <thead className="bg-e0e3-surface/60 text-e0e3-muted">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Fecha</th>
                      <th className="px-3 py-2 text-left font-medium">Nº venta</th>
                      <th className="px-3 py-2 text-right font-medium">Total</th>
                      <th className="px-3 py-2 text-right font-medium">Pagado</th>
                      <th className="px-3 py-2 text-right font-medium">Saldo</th>
                      <th className="px-3 py-2 text-right font-medium">Días</th>
                      <th className="px-3 py-2 text-left font-medium">Método</th>
                      <th className="px-3 py-2 text-right font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((fila) => (
                      <tr
                        key={fila.id}
                        className={`border-t border-e0e3-border/20 hover:bg-white/[0.03] ${
                          fila.vencida ? 'bg-red-500/[0.04]' : ''
                        }`}
                      >
                        <td className="px-3 py-2 text-e0e3-muted">{formatFecha(fila.fecha)}</td>
                        <td className="px-3 py-2 text-e0e3-foreground font-medium">{fila.numero}</td>
                        <td className="px-3 py-2 text-right text-e0e3-muted">{formatMoney(fila.total)}</td>
                        <td className="px-3 py-2 text-right text-emerald-300/90">{formatMoney(fila.pagado)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-e0e3-foreground">
                          {formatMoney(fila.saldo)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={fila.vencida ? 'text-red-200 font-medium' : 'text-e0e3-muted'}>
                            {fila.diasAtraso}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-e0e3-muted capitalize">
                          {fila.metodoPago ? formatMetodoPagoLabel(fila.metodoPago) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <Link to={`/ventas/${fila.id}`} className="inline-block mr-2">
                            <Button size="sm" color="secondary">Ver venta</Button>
                          </Link>
                          <Button
                            size="sm"
                            color="primary"
                            icon={<FaMoneyBillWave />}
                            onClick={() => abrirPago(fila)}
                          >
                            Registrar pago
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-e0e3-surface/40 text-e0e3-foreground">
                    <tr className="border-t border-e0e3-border/30">
                      <td colSpan={4} className="px-3 py-2 text-right font-medium">
                        Suma saldos
                      </td>
                      <td className="px-3 py-2 text-right font-bold">{formatMoney(resumen?.sumaFilas)}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="rounded-lg border border-e0e3-border/30 bg-e0e3-surface/30 p-6 text-center text-sm text-e0e3-muted">
                Formulario de cobro abierto. Cerrá el diálogo para ver el detalle de facturas.
              </div>
            </Card>
          )}
        </>
      )}

      <RegistrarPagoDialog
        isOpen={showPagoDialog}
        onClose={() => {
          setShowPagoDialog(false);
          setVentaPago(null);
          if (modoPagoDirectoRef.current) {
            navigate('/cuenta-corriente');
          }
        }}
        venta={ventaPago}
        onPagoRegistrado={registrarPago}
      />
    </div>
  );
};

export default CuentaCorrienteCliente;
